import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead, { SEO_KEYWORDS } from "../components/SeoHead";
import { useAuth } from "../context/AuthContext";

function Landing() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const inbox = new URLSearchParams(location.search).get("inbox");
    if (!inbox) return;
    if (isAuthenticated) {
      navigate(`/dashboard?inbox=${inbox}`, { replace: true });
    } else {
      navigate("/login", {
        replace: true,
        state: { from: { pathname: "/dashboard", search: `?inbox=${inbox}` } },
      });
    }
  }, [location.search, isAuthenticated, navigate]);

  const coreFeatures = [
    {
      tag: "Speed",
      title: "Instant addresses",
      desc: "Pick any username and receive mail at username.yoursubdomain@ghostinbox.xyz within seconds.",
    },
    {
      tag: "Real-time",
      title: "Live inbox",
      desc: "Messages refresh every 10 seconds so OTPs and verification links arrive on time.",
    },
    {
      tag: "Security",
      title: "Protect your personal inbox",
      desc: "Use Ghost Inbox for sign-ups, trials, and newsletters — keep your primary email private and reduce unwanted mail.",
    },
    {
      tag: "Personal",
      title: "Your own temporary inbox",
      desc: "Create a free account and use Ghost Inbox as a dedicated testing or privacy inbox. Messages are held only briefly — never kept as a permanent mailbox.",
    },
    {
      tag: "Security",
      title: "Private, not public",
      desc: "Unlike open temp-mail sites where anyone who guesses a username can read the inbox, Ghost Inbox is tied to your account — your mail is not exposed to strangers.",
    },
    {
      tag: "Privacy",
      title: "Deleted every 20 minutes",
      desc: "All emails are automatically removed after 20 minutes. We do not archive your message content — built-in expiry, not optional cleanup.",
    },
    {
      tag: "Premium",
      title: "Ad-free experience",
      desc: "A professional inbox experience with no banners, pop-ups, or distractions — just a clean workspace when you need it.",
    },
  ];

  const audienceFeatures = [
    {
      tag: "Developers",
      title: "Built for engineering workflows",
      desc: "Use temporary inboxes for API testing, email workflow validation, registration testing, and automated testing environments.",
    },
    {
      tag: "QA",
      title: "Validate every email touchpoint",
      desc: "Confirm signup emails, password reset emails, notification emails, and verification workflows without using personal email accounts.",
    },
    {
      tag: "Privacy",
      title: "Shield your primary inbox",
      desc: "Protect your personal inbox from marketing spam, promotional newsletters, data collection, and unwanted communications.",
    },
    {
      tag: "Infrastructure",
      title: "Fast & secure",
      desc: "Instant inbox creation, automatic cleanup, and secure infrastructure designed for reliability and professional use.",
    },
  ];

  return (
    <Layout>
      <SeoHead
        title="Ghost Inbox – Privacy-Focused Temporary Inbox for Testing & QA"
        description="Create secure temporary email inboxes for software testing, QA workflows, account verification testing, spam protection, and privacy-conscious browsing. Real-time delivery with automatic cleanup."
        path="/"
        keywords={SEO_KEYWORDS}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Ghost Inbox",
          url: "https://ghostinbox.xyz",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "Privacy-focused temporary inbox platform for software testing, QA workflows, and spam protection with real-time delivery and 20-minute auto-expiry.",
        }}
      />

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/20 via-zinc-950 to-zinc-950" />
        <div className="relative mx-auto max-w-6xl px-6 py-24 md:py-32">
          <p className="mb-4 inline-block rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1 text-xs font-medium uppercase tracking-wider text-emerald-400">
            Privacy-focused temporary inbox
          </p>
          <h1 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl lg:text-6xl">
            Privacy-Focused Temporary Inbox Solution
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-400">
            Create secure temporary email inboxes for software testing, QA workflows, account
            verification testing, spam protection, and privacy-conscious browsing.
          </p>
          <p className="mt-4 max-w-xl text-sm text-zinc-500">
            Professional addresses at{" "}
            <span className="font-mono text-zinc-300">you.yoursubdomain@ghostinbox.xyz</span> — account-linked,
            real-time delivery, and automatic message expiry.
          </p>
          <div className="mt-10">
            <div className="flex flex-wrap gap-4">
              <Link
                to="/signup"
                className="rounded-lg bg-emerald-500 px-8 py-3 text-sm font-semibold text-zinc-950 transition hover:bg-emerald-400"
              >
                Get started for free
              </Link>
              <Link
                to="/login"
                className="rounded-lg border border-zinc-600 px-8 py-3 text-sm font-medium text-white transition hover:border-zinc-400"
              >
                Login
              </Link>
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              No credit/debit card required for free account
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-zinc-800 bg-zinc-900/40">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">
              Why Ghost Inbox
            </p>
            <h2 className="mt-2 text-2xl font-bold text-white md:text-3xl">
              Professional temporary inboxes for testing and privacy
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Ghost Inbox is built for developers, QA teams, and privacy-conscious users who need a
              secure secondary inbox — without compromising their primary email or platform trust.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {audienceFeatures.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-emerald-500/20 bg-zinc-950/60 p-6"
              >
                <span className="inline-block rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-400">
                  {item.tag}
                </span>
                <h3 className="mt-3 text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {coreFeatures.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-6"
              >
                <span className="inline-block rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-400">
                  {item.tag}
                </span>
                <h3 className="mt-3 text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">
            Pricing
          </p>
          <h2 className="mt-2 text-2xl font-bold text-white md:text-3xl">
            Simple, transparent pricing
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            Start with a full-featured free trial. No credit card required — cancel anytime.
          </p>
        </div>

        <div className="mx-auto max-w-md">
          <div className="relative rounded-2xl border border-emerald-500/30 bg-gradient-to-b from-zinc-900 to-zinc-950 p-8 shadow-lg shadow-emerald-900/10">
            <span className="inline-block rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-emerald-400">
              Pro
            </span>
            <h3 className="mt-4 text-2xl font-bold text-white">Ghost Inbox Pro</h3>
            <p className="mt-2 text-sm text-zinc-400">
              Reserved inbox names, ad-free experience, and priority access for professional
              workflows.
            </p>
            <p className="mt-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold text-white">$4.99</span>
              <span className="text-sm text-zinc-500">/ month</span>
            </p>

            <ul className="mt-6 space-y-3 text-sm text-zinc-300">
              {[
                "Free for the first 30 days",
                "No credit card required to start",
                "Cancel anytime",
                "Reserved inbox names",
                "Ad-free experience",
                "Priority access",
                "Real-time inbox with automatic cleanup",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-0.5 text-emerald-400" aria-hidden="true">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <Link
              to="/signup"
              className="mt-8 block w-full rounded-lg bg-emerald-500 py-3 text-center text-sm font-semibold text-zinc-950 transition hover:bg-emerald-400"
            >
              Get started
            </Link>
            <p className="mt-3 text-center text-xs text-zinc-500">
              No credit/debit card required for free account
            </p>
            <Link
              to="/pricing"
              className="mt-4 block text-center text-sm text-emerald-400 underline-offset-4 hover:underline"
            >
              View all plans
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-10 text-center md:p-14">
          <h2 className="text-2xl font-bold text-white md:text-3xl">
            Ready to protect your inbox?
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-zinc-400">
            Create a free account to access your dashboard and start using secure temporary inboxes
            for testing, QA, and privacy protection.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/how-it-works"
              className="text-sm text-emerald-400 underline-offset-4 hover:underline"
            >
              Learn how it works
            </Link>
            <span className="text-zinc-600">·</span>
            <Link
              to="/about"
              className="text-sm text-emerald-400 underline-offset-4 hover:underline"
            >
              About Us
            </Link>
            <span className="text-zinc-600">·</span>
            <Link
              to="/contact"
              className="text-sm text-emerald-400 underline-offset-4 hover:underline"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Landing;
